<template>
  <div id="layout-wrapper">
    <NavigationBar />
    <div id="content" class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Header Section -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="setup-header">
                <div class="setup-header-left">
                  <div class="setup-badge">CONFIGURATION</div>
                  <h1 class="setup-title">Payroll Setup</h1>
                  <p class="setup-description">
                    Configure statutory rates, tax bands, and payroll groups for accurate monthly processing.
                  </p>
                </div>
                <button type="button" class="btn-hub" @click="$router.push('/payroll-hub')">
                  <i class="bx bx-arrow-back"></i>
                  Payroll Hub
                </button>
              </div>
            </div>
          </div>

          <!-- Info Alert -->
          <div class="info-alert">
            <i class="bx bx-info-circle"></i>
            <div>
              <strong>Setup workflow:</strong> 
              <span>Create payroll groups → assign employees to each group → configure statutory rates → <router-link to="/payroll-assign-salaries">Assign salaries per group</router-link> → Process payroll in <router-link to="/payroll-hub">Payroll Hub</router-link></span>
            </div>
          </div>

          <div class="info-alert">
            <i class="bx bx-info-circle"></i>
            <div>
              <strong>Automated setup:</strong>
              <span>New employees are auto-assigned to the default payroll group. Creating your first group backfills existing staff. Use <strong>Manage Members</strong> only when splitting payroll across multiple groups.</span>
            </div>
          </div>

          <!-- Payroll Groups Card -->
          <div class="setup-card">
            <div class="setup-card-header">
              <div class="header-icon blue">
                <i class="bx bx-group"></i>
              </div>
              <h5 class="setup-card-title">Payroll Groups</h5>
            </div>
            <div class="setup-card-body">
              <div class="create-group-row">
                <div class="create-group-inputs">
                  <div class="input-group">
                    <label class="input-label">Group Name</label>
                    <input v-model="newConfigName" type="text" placeholder="e.g., Head Office, Regional Branch" />
                  </div>
                  <div class="input-group small">
                    <label class="input-label">Currency</label>
                    <input v-model="newConfigCurrency" type="text" placeholder="KES" />
                  </div>
                </div>
                <div class="create-group-actions">
                  <button class="btn-primary" :disabled="loading" @click="createConfig">
                    <i class="bx bx-plus"></i> Create Group
                  </button>
                  <button class="btn-outline" :disabled="loading" @click="loadConfigs">
                    <i class="bx bx-refresh"></i> Refresh
                  </button>
                </div>
              </div>

              <div class="groups-table-wrapper" v-if="configs.length">
                <table class="data-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Group Name</th>
                      <th>Currency</th>
                      <th>Members</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="c in configs" :key="c.id">
                      <td class="id-cell">#{{ c.id }}</td>
                      <td>{{ c.config_name }}</td>
                      <td><span class="currency-badge">{{ c.currency }}</span></td>
                      <td>
                        <span class="member-count-badge">{{ c.employee_count || 0 }}</span>
                      </td>
                      <td class="action-cell">
                        <button class="icon-btn" @click="openMemberModal(c)">
                          <i class="bx bx-user-plus"></i> Manage Members
                        </button>
                        <button class="icon-btn" @click="$router.push({ path: '/payroll-hub', query: { config_id: c.id } })">
                          <i class="bx bx-arrow-right"></i> Open Hub
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="empty-state small">
                <i class="bx bx-folder"></i>
                <p>No payroll groups yet. Create one above.</p>
              </div>
            </div>
          </div>

          <!-- Statutory Configuration Card -->
          <div class="setup-card">
            <div class="setup-card-header">
              <div class="header-icon purple">
                <i class="bx bx-calculator"></i>
              </div>
              <h5 class="setup-card-title">Statutory Configuration</h5>
              <div class="header-actions">
                <button class="btn-outline-sm" :disabled="loading" @click="loadKenyanStatutory">
                  <i class="bx bx-cloud-download"></i> Reload
                </button>
                <button class="btn-primary-sm" :disabled="loading" @click="saveKenyanStatutory">
                  <i class="bx bx-save"></i> Save Settings
                </button>
              </div>
            </div>
            <div class="setup-card-body">
              <!-- Effective Dates -->
              <div class="dates-row">
                <div class="input-group">
                  <label class="input-label">Effective From</label>
                  <input v-model="st.effective_from" type="date" />
                </div>
                <div class="input-group">
                  <label class="input-label">Effective To <span class="optional">(optional)</span></label>
                  <input v-model="st.effective_to" type="date" />
                </div>
              </div>

              <!-- PAYE Section -->
              <div class="section-divider">
                <div class="section-icon">
                  <i class="bx bx-trending-up"></i>
                </div>
                <h6>PAYE (Monthly Taxable Pay)</h6>
                <div class="section-actions">
                  <button class="preset-btn" @click="applyKraPayePreset">
                    <i class="bx bx-refresh"></i> Apply KRA 5-Band Preset
                  </button>
                </div>
              </div>

              <div class="info-note">
                <i class="bx bx-info-circle"></i>
                <span>Active PAYE bands: <strong>{{ (st.paye_bands || []).length }}</strong>. Leave "Upper limit" empty on the last band for open-ended income.</span>
              </div>

              <div class="two-col-row">
                <div class="input-group">
                  <label class="input-label">Personal Relief (KES/month)</label>
                  <input v-model.number="st.paye_personal_relief" type="number" min="0" step="1" />
                </div>
                <div class="band-actions">
                  <button class="btn-outline-sm" @click="addPayeBand">
                    <i class="bx bx-plus"></i> Add Band
                  </button>
                  <button class="btn-outline-sm" @click="removeLastPayeBand" :disabled="!st.paye_bands || st.paye_bands.length <= 1">
                    <i class="bx bx-minus"></i> Remove Last
                  </button>
                </div>
              </div>

              <div class="table-wrapper">
                <table class="bands-table">
                  <thead>
                    <tr>
                      <th>From (KES min)</th>
                      <th>Up to (KES max)</th>
                      <th>Rate (%)</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(b, idx) in st.paye_bands" :key="'pb-' + idx">
                      <td><input v-model.number="b.min" type="number" min="0" step="1" /></td>
                      <td>
                        <input
                          v-model="b.maxInput"
                          type="number"
                          min="0"
                          step="1"
                          :placeholder="idx === st.paye_bands.length - 1 ? 'Open-ended' : 'Required'"
                        />
                      </td>
                      <td><input v-model.number="b.rate_pct" type="number" min="0" max="100" step="0.01" /></td>
                      <td>
                        <button class="remove-btn" @click="removePayeBand(idx)" :disabled="st.paye_bands.length <= 1">
                          <i class="bx bx-trash"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Extra Statutory Section -->
              <div class="section-divider">
                <div class="section-icon green">
                  <i class="bx bx-plus-circle"></i>
                </div>
                <h6>Extra Statutory Deductions</h6>
                <div class="section-actions">
                  <button class="preset-btn" @click="addExtraStatutory">
                    <i class="bx bx-plus"></i> Add Line
                  </button>
                </div>
              </div>

              <p class="field-note">Use for additional national or county levies calculated as a percentage of gross pay.</p>

              <div class="table-wrapper" v-if="st.extra_statutory && st.extra_statutory.length">
                <table class="bands-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Label</th>
                      <th>Rate (%)</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(x, xi) in st.extra_statutory" :key="'ex-' + xi">
                      <td><input v-model.trim="x.code" type="text" placeholder="e.g., COUNTY_LEVY" /></td>
                      <td><input v-model.trim="x.label" type="text" placeholder="Display name" /></td>
                      <td><input v-model.number="x.rate_pct" type="number" min="0" max="100" step="0.01" /></td>
                      <td>
                        <button class="remove-btn" @click="removeExtraStatutory(xi)">
                          <i class="bx bx-trash"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="empty-state minimal">
                <p>No extra statutory lines configured. Only PAYE, NSSF, SHIF, and Housing Levy will apply.</p>
              </div>

              <!-- NSSF Section -->
              <div class="section-divider">
                <div class="section-icon orange">
                  <i class="bx bx-shield"></i>
                </div>
                <h6>NSSF (Tier 1 & 2)</h6>
                <div class="section-actions">
                  <button class="preset-btn" @click="applyNssfPreset">
                    <i class="bx bx-refresh"></i> Apply NSSF Preset (108,000 ceiling)
                  </button>
                </div>
              </div>

              <div class="four-col-row">
                <div class="input-group">
                  <label class="input-label">Lower Limit (KES)</label>
                  <input v-model.number="st.nssf_lel" type="number" min="0" step="1" />
                </div>
                <div class="input-group">
                  <label class="input-label">Pensionable Ceiling (KES)</label>
                  <input v-model.number="st.nssf_uel" type="number" min="1" step="1" />
                </div>
                <div class="input-group">
                  <label class="input-label">Employee Rate (%)</label>
                  <input v-model.number="st.nssf_employee_rate_pct" type="number" min="0" max="100" step="0.01" />
                </div>
                <div class="input-group">
                  <label class="input-label">Max Employee Contribution (KES)</label>
                  <input v-model.number="st.nssf_employee_cap" type="number" min="0" step="1" />
                </div>
              </div>

              <!-- SHA/SHIF Section -->
              <div class="section-divider">
                <div class="section-icon teal">
                  <i class="bx bx-heart"></i>
                </div>
                <h6>SHA / SHIF (Health Insurance)</h6>
              </div>

              <div class="two-col-row">
                <div class="input-group">
                  <label class="input-label">Rate (% of Gross Pay)</label>
                  <input v-model.number="st.sha_rate_pct" type="number" min="0" max="100" step="0.01" />
                </div>
              </div>

              <!-- Housing Levy Section -->
              <div class="section-divider">
                <div class="section-icon red">
                  <i class="bx bx-home"></i>
                </div>
                <h6>Housing Levy</h6>
              </div>

              <div class="two-col-row">
                <div class="input-group">
                  <label class="input-label">Rate (% of Gross Pay)</label>
                  <input v-model.number="st.housing_rate_pct" type="number" min="0" max="100" step="0.01" />
                </div>
              </div>
            </div>
          </div>

          <!-- Message Alert -->
          <div v-if="message" class="message-alert">
            <i class="bx bx-check-circle"></i>
            <span>{{ message }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Payroll group members modal -->
    <Teleport to="body">
      <div v-if="memberModalOpen" class="pg-modal-backdrop" @click.self="closeMemberModal">
        <div class="pg-modal" role="dialog" aria-modal="true" :aria-labelledby="memberModalTitleId">
          <div class="pg-modal-header">
            <div>
              <h5 class="pg-modal-title" :id="memberModalTitleId">
                Manage members — {{ memberConfig?.config_name || 'Payroll group' }}
              </h5>
              <p class="pg-modal-subtitle">Assign employees to this group. Each employee can belong to one group at a time.</p>
            </div>
            <button type="button" class="pg-modal-close" aria-label="Close" @click="closeMemberModal">
              <i class="bx bx-x"></i>
            </button>
          </div>

          <div class="pg-modal-body">
            <div class="pg-section">
              <div class="pg-section-head">
                <h6>Current members ({{ currentMembers.length }})</h6>
                <button type="button" class="btn-outline-sm" :disabled="memberLoading" @click="loadMemberData">
                  <i class="bx bx-refresh"></i> Refresh
                </button>
              </div>
              <div v-if="memberLoading && !currentMembers.length" class="pg-loading">Loading members…</div>
              <div v-else-if="!currentMembers.length" class="empty-state minimal">
                <p>No employees in this group yet. Add staff below.</p>
              </div>
              <div v-else class="pg-member-list">
                <div v-for="m in currentMembers" :key="'m-' + m.id" class="pg-member-row">
                  <div class="pg-member-info">
                    <strong>{{ memberName(m) }}</strong>
                    <span class="pg-member-meta">{{ m.empNo || '—' }} · {{ m.departmentName || 'No department' }}</span>
                  </div>
                  <button
                    type="button"
                    class="pg-remove-btn"
                    :disabled="memberLoading"
                    @click="removeMember(m.id)"
                  >
                    <i class="bx bx-user-minus"></i> Remove
                  </button>
                </div>
              </div>
            </div>

            <div class="pg-section pg-add-section">
              <div class="pg-section-head">
                <h6>Add employees</h6>
              </div>
              <div class="pg-search-row">
                <input
                  v-model="memberSearch"
                  type="search"
                  placeholder="Search by name, employee no., or email…"
                  @input="debouncedMemberSearch"
                  @keyup.enter="loadMemberCandidates"
                />
                <button type="button" class="btn-primary-sm" :disabled="memberLoading" @click="loadMemberCandidates">
                  <i class="bx bx-search"></i> Search
                </button>
              </div>

              <div v-if="memberLoading && !memberCandidates.length" class="pg-loading">Loading employees…</div>
              <div v-else-if="!memberCandidates.length" class="empty-state minimal">
                <p>Search to find active employees to add.</p>
              </div>
              <div v-else class="pg-candidate-list">
                <label
                  v-for="emp in memberCandidates"
                  :key="'c-' + emp.id"
                  class="pg-candidate-row"
                  :class="{ disabled: emp['in_this_group'] == 1, 'in-other': emp.other_group_name }"
                >
                  <input
                    type="checkbox"
                    :value="emp.id"
                    v-model="selectedToAdd"
                    :disabled="emp['in_this_group'] == 1 || memberLoading"
                  />
                  <div class="pg-candidate-info">
                    <strong>{{ memberName(emp) }}</strong>
                    <span class="pg-member-meta">
                      {{ emp.empNo || '—' }} · {{ emp.departmentName || 'No department' }}
                      <span v-if="emp['in_this_group'] == 1" class="pg-tag in-group">In this group</span>
                      <span v-else-if="emp.other_group_name" class="pg-tag other-group">In {{ emp.other_group_name }}</span>
                    </span>
                  </div>
                </label>
              </div>

              <div class="pg-modal-footer">
                <span class="pg-selected-count">{{ selectedToAdd.length }} selected</span>
                <div class="pg-footer-actions">
                  <button type="button" class="btn-outline" @click="closeMemberModal">Close</button>
                  <button
                    type="button"
                    class="btn-primary"
                    :disabled="memberLoading || !selectedToAdd.length"
                    @click="assignSelected"
                  >
                    <i class="bx bx-user-plus"></i>
                    {{ memberLoading ? 'Assigning…' : 'Assign selected' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, execute } from '@/api';
import NavigationBar from '@/components/Navigation.vue';

function defaultStatutory() {
  const now = new Date();
  return {
    effective_from: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`,
    effective_to: '',
    paye_personal_relief: 2400,
    paye_bands: [
      { min: 0, maxInput: '24000', rate_pct: 10 },
      { min: 24000, maxInput: '32333', rate_pct: 25 },
      { min: 32333, maxInput: '500000', rate_pct: 30 },
      { min: 500000, maxInput: '800000', rate_pct: 32.5 },
      { min: 800000, maxInput: '', rate_pct: 35 }
    ],
    extra_statutory: [],
    nssf_lel: 7000,
    nssf_uel: 36000,
    nssf_employee_rate_pct: 6,
    nssf_employee_cap: 2160,
    sha_rate_pct: 2.75,
    housing_rate_pct: 1.5
  };
}

function hasValidPayeBands(bands) {
  return Array.isArray(bands) && bands.length > 0;
}

function normalizePayeBandsFromApi(bands) {
  if (!hasValidPayeBands(bands)) return defaultStatutory().paye_bands;
  return bands.map((b, idx, arr) => {
    const last = idx === arr.length - 1;
    const maxVal = b.max;
    const open = maxVal === null || maxVal === undefined || maxVal === '';
    return {
      min: Number(b.min) || 0,
      maxInput: open && last ? '' : String(maxVal ?? ''),
      rate_pct: Number(b.rate_pct) || 0
    };
  });
}

export default {
  name: 'KenyanPayrollSetup',
  components: { NavigationBar },
  data() {
    const now = new Date();
    return {
      loading: false,
      message: '',
      configs: [],
      newConfigName: '',
      newConfigCurrency: 'KES',
      st: defaultStatutory(),
      memberModalOpen: false,
      memberConfig: null,
      memberSearch: '',
      memberLoading: false,
      currentMembers: [],
      memberCandidates: [],
      selectedToAdd: [],
      memberSearchTimer: null,
      memberModalTitleId: 'payroll-group-members-title'
    };
  },
  mounted() {
    this.loadConfigs();
    this.loadKenyanStatutory();
  },
  beforeUnmount() {
    if (this.memberSearchTimer) clearTimeout(this.memberSearchTimer);
  },
  methods: {
    formatMoney(v) {
      return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 2 }).format(Number(v) || 0);
    },
    setMsg(t) {
      this.message = t;
      setTimeout(() => { if (this.message === t) this.message = ''; }, 4000);
    },
    async post(fn, extra = {}) {
      const data = new FormData();
      data.append('function', fn);
      Object.keys(extra).forEach((k) => {
        const v = extra[k];
        if (v !== undefined && v !== null && v !== '') data.append(k, v);
      });
      return execute(data, authUrl);
    },
    async loadConfigs() {
      this.loading = true;
      try {
        const res = await this.post('getPayrollConfigs');
        if (res.data?.success) {
          this.configs = res.data.data?.payroll_configs || [];
          const autoAssigned = Number(res.data.data?.auto_assigned || 0);
          if (autoAssigned > 0) {
            this.setMsg(`${autoAssigned} unassigned employee(s) were auto-added to the default payroll group.`);
          }
        } else this.setMsg(res.data?.message || 'Failed to load configs');
      } catch (e) {
        this.setMsg('Failed to load configs');
      } finally {
        this.loading = false;
      }
    },
    memberName(emp) {
      return [emp.firstName, emp.middleName, emp.lastName].filter(Boolean).join(' ') || '—';
    },
    openMemberModal(config) {
      this.memberConfig = config;
      this.memberModalOpen = true;
      this.memberSearch = '';
      this.selectedToAdd = [];
      this.memberCandidates = [];
      this.loadMemberData();
      this.loadMemberCandidates();
    },
    closeMemberModal() {
      this.memberModalOpen = false;
      this.memberConfig = null;
      this.currentMembers = [];
      this.memberCandidates = [];
      this.selectedToAdd = [];
    },
    async loadMemberData() {
      if (!this.memberConfig?.id) return;
      this.memberLoading = true;
      try {
        const res = await this.post('getPayrollGroupMembers', { payroll_config_id: this.memberConfig.id });
        if (res.data?.success) {
          this.currentMembers = res.data.data?.members || [];
        } else {
          this.setMsg(res.data?.message || 'Failed to load members');
        }
      } catch (e) {
        this.setMsg('Failed to load members');
      } finally {
        this.memberLoading = false;
      }
    },
    debouncedMemberSearch() {
      if (this.memberSearchTimer) clearTimeout(this.memberSearchTimer);
      this.memberSearchTimer = setTimeout(() => this.loadMemberCandidates(), 350);
    },
    async loadMemberCandidates() {
      if (!this.memberConfig?.id) return;
      this.memberLoading = true;
      try {
        const res = await this.post('getEmployeesForPayrollGroupAssign', {
          payroll_config_id: this.memberConfig.id,
          keyword: this.memberSearch.trim()
        });
        if (res.data?.success) {
          this.memberCandidates = res.data.data?.employees || [];
        }
      } catch (e) {
        this.setMsg('Failed to load employees');
      } finally {
        this.memberLoading = false;
      }
    },
    async assignSelected() {
      if (!this.memberConfig?.id || !this.selectedToAdd.length) return;
      this.memberLoading = true;
      try {
        const res = await this.post('bulkAssignEmployeesToPayrollConfig', {
          payroll_config_id: this.memberConfig.id,
          employee_ids: JSON.stringify(this.selectedToAdd),
          exclusive: '1'
        });
        if (res.data?.success) {
          this.setMsg(res.data.message || 'Employees assigned');
          this.selectedToAdd = [];
          await this.loadMemberData();
          await this.loadMemberCandidates();
          await this.loadConfigs();
        } else {
          this.setMsg(res.data?.message || 'Assign failed');
        }
      } catch (e) {
        this.setMsg('Assign failed');
      } finally {
        this.memberLoading = false;
      }
    },
    async removeMember(employeeId) {
      if (!this.memberConfig?.id || !employeeId) return;
      this.memberLoading = true;
      try {
        const res = await this.post('removeEmployeeFromPayrollConfig', {
          payroll_config_id: this.memberConfig.id,
          employee_id: employeeId
        });
        if (res.data?.success) {
          this.setMsg(res.data.message || 'Employee removed');
          await this.loadMemberData();
          await this.loadMemberCandidates();
          await this.loadConfigs();
        } else {
          this.setMsg(res.data?.message || 'Remove failed');
        }
      } catch (e) {
        this.setMsg('Remove failed');
      } finally {
        this.memberLoading = false;
      }
    },
    async createConfig() {
      if (!this.newConfigName.trim()) {
        this.setMsg('Enter a group name');
        return;
      }
      this.loading = true;
      try {
        const res = await this.post('createPayrollConfig', {
          config_name: this.newConfigName.trim(),
          currency: this.newConfigCurrency.trim() || 'KES'
        });
        if (res.data?.success) {
          this.setMsg(res.data.message || 'Created');
          this.newConfigName = '';
          await this.loadConfigs();
        } else this.setMsg(res.data?.message || 'Create failed');
      } catch (e) {
        this.setMsg('Create failed');
      } finally {
        this.loading = false;
      }
    },
    async loadKenyanStatutory() {
      this.loading = true;
      try {
        const res = await this.post('getKenyanStatutoryForm', { as_of: new Date().toISOString().slice(0, 10) });
        if (res.data?.success && res.data.data?.form) {
          const f = res.data.data.form;
          this.st = { ...defaultStatutory(), ...f };
          this.st.paye_bands = hasValidPayeBands(f.paye_bands)
            ? normalizePayeBandsFromApi(f.paye_bands)
            : defaultStatutory().paye_bands;
          if (!Array.isArray(this.st.extra_statutory)) this.st.extra_statutory = [];
          this.setMsg('Statutory form loaded from database.');
        } else this.setMsg(res.data?.message || 'Failed to load statutory form');
      } catch (e) {
        this.setMsg('Failed to load statutory form');
      } finally {
        this.loading = false;
      }
    },
    serializePayeBandsForSave() {
      const rows = this.st.paye_bands || [];
      return rows.map((b, idx) => {
        const last = idx === rows.length - 1;
        const raw = b.maxInput;
        const empty = raw === '' || raw === null || raw === undefined;
        if (!last && empty) {
          throw new Error('PAYE: every band except the last needs an upper limit.');
        }
        const max = empty && last ? null : Number(raw);
        if (!empty && (Number.isNaN(max) || max <= Number(b.min))) {
          throw new Error('PAYE: each band max must be greater than min.');
        }
        return {
          min: Number(b.min) || 0,
          max,
          rate_pct: Number(b.rate_pct) || 0
        };
      });
    },
    statutorySavePayload() {
      const bands = this.serializePayeBandsForSave();
      return {
        effective_from: this.st.effective_from,
        effective_to: this.st.effective_to || '',
        paye_personal_relief: this.st.paye_personal_relief,
        paye_bands_json: JSON.stringify(bands),
        extra_statutory_json: JSON.stringify(this.st.extra_statutory || []),
        nssf_lel: this.st.nssf_lel,
        nssf_uel: this.st.nssf_uel,
        nssf_employee_rate_pct: this.st.nssf_employee_rate_pct,
        nssf_employee_cap: this.st.nssf_employee_cap,
        sha_rate_pct: this.st.sha_rate_pct,
        housing_rate_pct: this.st.housing_rate_pct
      };
    },
    addPayeBand() {
      const rows = this.st.paye_bands || [];
      const last = rows[rows.length - 1];
      const prevMax = last && last.maxInput !== '' && last.maxInput != null ? Number(last.maxInput) : null;
      const startMin = prevMax != null && !Number.isNaN(prevMax) ? prevMax + 1 : (last ? Number(last.min) + 10000 : 0);
      this.st.paye_bands.push({ min: startMin, maxInput: '', rate_pct: 0 });
    },
    removePayeBand(idx) {
      if (this.st.paye_bands.length <= 1) return;
      this.st.paye_bands.splice(idx, 1);
    },
    removeLastPayeBand() {
      if (this.st.paye_bands.length <= 1) return;
      this.st.paye_bands.pop();
    },
    addExtraStatutory() {
      if (!Array.isArray(this.st.extra_statutory)) this.st.extra_statutory = [];
      this.st.extra_statutory.push({ code: '', label: '', rate_pct: 0 });
    },
    removeExtraStatutory(idx) {
      this.st.extra_statutory.splice(idx, 1);
    },
    applyKraPayePreset() {
      this.st.paye_bands = [
        { min: 0, maxInput: '24000', rate_pct: 10 },
        { min: 24000, maxInput: '32333', rate_pct: 25 },
        { min: 32333, maxInput: '500000', rate_pct: 30 },
        { min: 500000, maxInput: '800000', rate_pct: 32.5 },
        { min: 800000, maxInput: '', rate_pct: 35 }
      ];
      this.setMsg('Applied KRA 5-band PAYE preset. Click Save to persist.');
    },
    applyNssfPreset() {
      this.st.nssf_lel = 9000;
      this.st.nssf_uel = 108000;
      this.st.nssf_employee_rate_pct = 6;
      this.st.nssf_employee_cap = 6480;
      this.setMsg('Applied NSSF preset (UEL 108,000, employee cap 6,480). Click Save to persist.');
    },
    async saveKenyanStatutory() {
      this.loading = true;
      try {
        let payload;
        try {
          payload = this.statutorySavePayload();
        } catch (err) {
          this.setMsg(err.message || 'Check PAYE bands');
          this.loading = false;
          return;
        }
        const res = await this.post('saveKenyanStatutoryForm', payload);
        if (res.data?.success) {
          this.setMsg(res.data.message || 'Saved successfully');
          await this.loadKenyanStatutory();
        } else this.setMsg(res.data?.message || 'Save failed');
      } catch (e) {
        this.setMsg('Save failed');
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
/* Layout */
.content-minimize {
  margin-left: 250px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #f5f7fc 0%, #eef2f8 100%);
  min-height: 100vh;
}

@media (max-width: 992px) {
  .content-minimize {
    margin-left: 0;
    padding: 16px;
  }
}

/* Header */
.setup-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
}

.setup-header-left {
  flex: 1;
}

.setup-badge {
  display: inline-block;
  background: linear-gradient(135deg, #4f46e5, #6366f1);
  color: white;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
}

.setup-title {
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 6px 0;
  letter-spacing: -0.02em;
}

.setup-description {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.btn-hub {
  background: white;
  border: 1px solid #e2e8f0;
  padding: 10px 20px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.btn-hub:hover {
  border-color: #4f46e5;
  color: #4f46e5;
  background: #f8fafc;
}

/* Info Alert */
.info-alert {
  background: #eff6ff;
  border-radius: 14px;
  padding: 14px 18px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  color: #1e40af;
  border: 1px solid #bfdbfe;
}

.info-alert i {
  font-size: 20px;
}

.info-alert a {
  color: #4f46e5;
  text-decoration: none;
  font-weight: 500;
}

.info-alert a:hover {
  text-decoration: underline;
}

/* Cards */
.setup-card {
  background: white;
  border-radius: 24px;
  border: 1px solid #eef2ff;
  overflow: hidden;
  margin-bottom: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
}

.setup-card-header {
  padding: 20px 24px;
  border-bottom: 1px solid #eef2ff;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.header-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: white;
}

.header-icon.blue { background: linear-gradient(135deg, #3b82f6, #2563eb); }
.header-icon.purple { background: linear-gradient(135deg, #8b5cf6, #7c3aed); }
.header-icon.green { background: linear-gradient(135deg, #10b981, #059669); }
.header-icon.orange { background: linear-gradient(135deg, #f59e0b, #d97706); }
.header-icon.teal { background: linear-gradient(135deg, #14b8a6, #0d9488); }
.header-icon.red { background: linear-gradient(135deg, #ef4444, #dc2626); }

.setup-card-title {
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
  flex: 1;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.setup-card-body {
  padding: 20px 24px;
}

/* Create Group Row */
.create-group-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eef2ff;
}

.create-group-inputs {
  display: flex;
  gap: 16px;
  flex: 2;
  flex-wrap: wrap;
}

.create-group-inputs .input-group {
  flex: 1;
  min-width: 160px;
}

.create-group-actions {
  display: flex;
  gap: 12px;
}

/* Input Groups */
.input-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.input-label {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.input-label .optional {
  font-weight: 400;
  color: #94a3b8;
}

.input-group input, .input-group select {
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  color: #1e293b;
  background: white;
  transition: all 0.2s;
}

.input-group input:focus, .input-group select:focus {
  outline: none;
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

/* Buttons */
.btn-primary, .btn-primary-sm, .btn-outline, .btn-outline-sm, .preset-btn {
  padding: 8px 18px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-primary {
  background: #4f46e5;
  border: none;
  color: white;
}

.btn-primary:hover {
  background: #6366f1;
  transform: translateY(-1px);
}

.btn-primary-sm {
  background: #4f46e5;
  border: none;
  color: white;
  padding: 6px 14px;
  font-size: 12px;
}

.btn-primary-sm:hover {
  background: #6366f1;
}

.btn-outline {
  background: transparent;
  border: 1px solid #cbd5e1;
  color: #475569;
}

.btn-outline:hover {
  border-color: #4f46e5;
  color: #4f46e5;
  background: #f8fafc;
}

.btn-outline-sm {
  background: transparent;
  border: 1px solid #cbd5e1;
  color: #475569;
  padding: 6px 14px;
  font-size: 12px;
}

.btn-outline-sm:hover {
  border-color: #4f46e5;
  color: #4f46e5;
  background: #f8fafc;
}

.preset-btn {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #475569;
  padding: 6px 14px;
  font-size: 12px;
}

.preset-btn:hover {
  background: #eef2ff;
  border-color: #4f46e5;
  color: #4f46e5;
}

/* Tables */
.groups-table-wrapper, .table-wrapper {
  overflow-x: auto;
  border-radius: 16px;
  border: 1px solid #eef2ff;
  margin-top: 8px;
}

.data-table, .bands-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.data-table th, .bands-table th {
  text-align: left;
  padding: 12px 16px;
  background: #f8fafc;
  color: #475569;
  font-weight: 600;
  border-bottom: 1px solid #eef2ff;
}

.data-table td, .bands-table td {
  padding: 10px 16px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
}

.id-cell {
  font-weight: 600;
  color: #4f46e5;
}

.currency-badge {
  display: inline-block;
  background: #f1f5f9;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  color: #475569;
}

.bands-table input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
}

.bands-table input:focus {
  outline: none;
  border-color: #4f46e5;
}

.remove-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  font-size: 18px;
  padding: 4px;
  border-radius: 6px;
}

.remove-btn:hover {
  color: #ef4444;
  background: #fef2f2;
}

/* Section Dividers */
.section-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 28px 0 16px 0;
  padding-top: 8px;
  border-top: 1px solid #eef2ff;
}

.section-divider:first-of-type {
  margin-top: 0;
  border-top: none;
}

.section-icon {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: white;
}

.section-icon.green { background: linear-gradient(135deg, #10b981, #059669); }
.section-icon.orange { background: linear-gradient(135deg, #f59e0b, #d97706); }
.section-icon.teal { background: linear-gradient(135deg, #14b8a6, #0d9488); }
.section-icon.red { background: linear-gradient(135deg, #ef4444, #dc2626); }

.section-divider h6 {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  flex: 1;
}

.section-actions {
  display: flex;
  gap: 10px;
}

/* Info Note */
.info-note {
  background: #f8fafc;
  border-radius: 12px;
  padding: 10px 14px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: #475569;
  border: 1px solid #eef2ff;
}

.info-note i {
  color: #4f46e5;
  font-size: 16px;
}

.field-note {
  font-size: 12px;
  color: #64748b;
  margin: 0 0 12px 0;
}

/* Layout Rows */
.dates-row, .two-col-row, .four-col-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
}

.dates-row .input-group {
  flex: 1;
  min-width: 200px;
}

.two-col-row .input-group {
  flex: 1;
  min-width: 180px;
}

.four-col-row .input-group {
  flex: 1;
  min-width: 150px;
}

.band-actions {
  display: flex;
  gap: 10px;
  align-items: flex-end;
}

/* Empty States */
.empty-state {
  text-align: center;
  padding: 32px 24px;
  color: #94a3b8;
}

.empty-state i {
  font-size: 36px;
  margin-bottom: 12px;
  display: block;
}

.empty-state p {
  margin: 0;
  font-size: 13px;
}

.empty-state.minimal {
  padding: 20px;
  background: #f8fafc;
  border-radius: 12px;
  margin-top: 8px;
}

/* Message Alert */
.message-alert {
  background: #d1fae5;
  border-radius: 12px;
  padding: 12px 16px;
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #065f46;
  border: 1px solid #a7f3d0;
}

.message-alert i {
  font-size: 18px;
}

/* Action Cell */
.action-cell .icon-btn {
  background: transparent;
  border: none;
  color: #4f46e5;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
}

.action-cell .icon-btn:hover {
  background: #eef2ff;
}

.member-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  padding: 4px 10px;
  border-radius: 20px;
  background: #eef2ff;
  color: #4f46e5;
  font-size: 12px;
  font-weight: 700;
}

.action-cell {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

/* Member modal */
.pg-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.pg-modal {
  background: white;
  border-radius: 20px;
  width: min(720px, 100%);
  max-height: min(90vh, 900px);
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.18);
  overflow: hidden;
}

.pg-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid #eef2ff;
}

.pg-modal-title {
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

.pg-modal-subtitle {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}

.pg-modal-close {
  background: #f8fafc;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 22px;
  color: #64748b;
}

.pg-modal-body {
  overflow-y: auto;
  padding: 20px 24px 0;
}

.pg-section {
  margin-bottom: 24px;
}

.pg-section-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.pg-section-head h6 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.pg-member-list,
.pg-candidate-list {
  border: 1px solid #eef2ff;
  border-radius: 14px;
  overflow: hidden;
  max-height: 220px;
  overflow-y: auto;
}

.pg-member-row,
.pg-candidate-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid #f1f5f9;
}

.pg-candidate-row {
  cursor: pointer;
}

.pg-candidate-row.disabled {
  opacity: 0.65;
  cursor: default;
}

.pg-candidate-row.in-other:not(.disabled) {
  background: #fffbeb;
}

.pg-member-info,
.pg-candidate-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.pg-member-meta {
  font-size: 12px;
  color: #64748b;
}

.pg-remove-btn {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}

.pg-search-row {
  display: flex;
  gap: 10px;
  margin-bottom: 12px;
}

.pg-search-row input {
  flex: 1;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
}

.pg-tag {
  display: inline-block;
  margin-left: 6px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}

.pg-tag.in-group {
  background: #d1fae5;
  color: #065f46;
}

.pg-tag.other-group {
  background: #fef3c7;
  color: #92400e;
}

.pg-modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 16px 0 20px;
  border-top: 1px solid #eef2ff;
  margin-top: 8px;
}

.pg-selected-count {
  font-size: 13px;
  color: #64748b;
}

.pg-footer-actions {
  display: flex;
  gap: 10px;
}

.pg-loading {
  padding: 20px;
  text-align: center;
  color: #64748b;
  font-size: 13px;
}

/* Responsive */
@media (max-width: 768px) {
  .setup-card-body {
    padding: 16px;
  }
  
  .create-group-row {
    flex-direction: column;
    align-items: stretch;
  }
  
  .create-group-inputs {
    flex-direction: column;
  }
  
  .create-group-actions {
    justify-content: flex-end;
  }
  
  .dates-row, .two-col-row, .four-col-row {
    flex-direction: column;
  }
  
  .band-actions {
    justify-content: flex-start;
  }
  
  .header-actions {
    width: 100%;
    justify-content: flex-start;
  }
  
  .setup-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>